import { IconChevronLeft, IconChevronRight, useLocalization } from '@magicdoorinc/ui';
import { Show } from 'solid-js';
import { Portal } from 'solid-js/web';
import LogoGlow from '~/assets/images/sidebar/logoGlow.svg';
import { LinkToWebsite } from '~/components/layouts/Menu/components/LinkToWebsite';
import { IS_HOA_DEMO_MODE } from '~/utils/hoaDemoMode';
import { getPublicFileURL } from '~/utils/prefix';
import MainMenuItems from './MainMenuItems';
import SubMenuItems from './SubMenuItems';
import { ReferralCard } from './components/ReferralCard';
import { useMenuLogic } from './useMenuLogic';
import type { JSX } from 'solid-js';

export interface MenuThemeConfig {
  containerBgClass?: string;
  containerStyle?: Record<string, string>;
  collapseButtonZIndex?: boolean;
  activeColor: string;
  selectedColor?: string;
  panelColor?: string;
  popoverColor?: string;
  tooltipBgColor: string;
  linkTooltipBgColor: string;
  decoration?: JSX.Element;
}

export const BaseMenu = (props: { config: MenuThemeConfig }) => {
  const { t } = useLocalization();
  const menuLogic = useMenuLogic();
  const { menu, isSidebarOpen, setIsSidebarOpen, toggleMenuCollapse, scrollHeight } = menuLogic;

  const bgClass = () => props.config.containerBgClass ?? '';

  return (
    <Portal>
      <div
        class="fixed inset-0 z-30 bg-black/50 transition-opacity md:hidden"
        classList={{
          'opacity-0 pointer-events-none': !isSidebarOpen(),
          'opacity-100': isSidebarOpen(),
        }}
        onClick={() => setIsSidebarOpen(false)}
      />

      <div
        class={`fixed left-0 top-0 z-40 flex h-dvh -translate-x-full flex-col ${bgClass()} text-sm transition-transform md:translate-x-0`}
        classList={{
          'translate-x-0 w-[245px] md:w-64': isSidebarOpen() && !menu?.isManuallyCollapsed(),
          'translate-x-0 w-[245px] md:w-[68px]': isSidebarOpen() && menu?.isManuallyCollapsed(),
        }}
        ref={menuLogic.setRefMenuContainer}
        style={props.config.containerStyle}>
        <div class="relative flex items-center justify-between">
          <img
            src={!menu?.isManuallyCollapsed() || !isSidebarOpen() ? getPublicFileURL('/logo.png') : getPublicFileURL('/logoIcon.png')}
            class="sticky top-0 z-20 mx-auto select-none pb-6 pt-4"
            classList={{
              'w-[130px]': !menu?.isManuallyCollapsed() || !isSidebarOpen(),
              'w-[42px]': menu?.isManuallyCollapsed() && isSidebarOpen() && !menu.isMobile(),
              'mt-[55px]': menu?.isManuallyCollapsed() && isSidebarOpen(),
            }}
            alt="logo"
          />
          <button
            id="sidebar-toggle-collapse-btn"
            onClick={toggleMenuCollapse}
            class="absolute right-4 top-4 hidden rounded-md border border-border bg-white/10 p-1 text-white/70 transition-all hover-allowed:hover:text-white md:block"
            classList={{ 'z-10': props.config.collapseButtonZIndex ?? false }}>
            {menu?.isManuallyCollapsed() ? <IconChevronRight /> : <IconChevronLeft />}
          </button>
        </div>
        <img src={LogoGlow} class="z-20 mx-auto -mt-6 select-none" alt="" />
        <Show when={IS_HOA_DEMO_MODE && !menu?.isManuallyCollapsed()}>
          <div class="z-20 mb-2 text-center text-xs font-semibold uppercase tracking-[0.18em] text-emerald-200/90">
            {t('HOA Management')}
          </div>
        </Show>
        <div
          class="z-20 mx-auto h-px w-4/5"
          style={{
            background: IS_HOA_DEMO_MODE
              ? 'linear-gradient(90deg, rgba(31, 138, 106, 0) 0%, #1F8A6A 50%, rgba(31, 138, 106, 0) 100%)'
              : 'linear-gradient(90deg, rgba(90, 61, 147, 0) 0%, #5A3D93 50%, rgba(90, 61, 147, 0) 100%)',
          }}
        />

        {props.config.decoration}

        <div
          style={{
            '--menu-active-color': props.config.activeColor,
            '--menu-selected-color': props.config.selectedColor ?? props.config.activeColor,
            '--menu-panel-color': props.config.panelColor ?? '#2B0A53',
          }}
          ref={menuLogic.setScrollContainerRef}
          class="thinscroll pre flex flex-1 flex-col overflow-y-auto overflow-x-hidden scroll-smooth">
          <div ref={menuLogic.setMenuItemsRef} class="flex shrink-0 grow">
            <MainMenuItems
              setIsSidebarOpen={setIsSidebarOpen}
              isSidebarOpen={isSidebarOpen()}
              isMenuCollapsed={menu?.isManuallyCollapsed() ?? false}
              tooltipBgColor={props.config.tooltipBgColor}
              popoverColor={props.config.popoverColor ?? props.config.panelColor ?? '#2B0A53'}
              activeColor={props.config.activeColor}
            />
            <SubMenuItems setIsSidebarOpen={setIsSidebarOpen} containerHeight={scrollHeight()} />
          </div>

          <div class="mt-auto shrink-0">
            <Show when={!IS_HOA_DEMO_MODE}>
              <LinkToWebsite tooltipBgColor={props.config.linkTooltipBgColor} isMenuCollapsed={menu?.isManuallyCollapsed()} />
            </Show>
            <Show when={!menu?.isManuallyCollapsed() && !IS_HOA_DEMO_MODE}>
              <ReferralCard />
            </Show>
          </div>
        </div>
      </div>
    </Portal>
  );
};
